import React from "react";
import { Link } from "react-router";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { useGetSnapshotCollectionsQuery } from "@/modules/snapshots/snapshotCollection.api";
import type { BusinessReportOutcomeDetails } from "@/modules/snapshots/businessReports.types";

interface Props {
  businessReportOutcome: BusinessReportOutcomeDetails | null;
}

export const BusinessReportOutcomeBreadcrumbs: React.FC<Props> = ({ businessReportOutcome }) => {
  const { data: collections } = useGetSnapshotCollectionsQuery(
    businessReportOutcome?.business_report_id
      ? { business_report_id: businessReportOutcome.business_report_id }
      : undefined,
    {
      skip: !businessReportOutcome?.snapshot_collection_id,
    },
  );

  if (!businessReportOutcome) {
    return null;
  }

  const hasCollection = !!businessReportOutcome.snapshot_collection_id;
  const collection = collections?.find(c => c.id === businessReportOutcome.snapshot_collection_id);
  const collectionName = collection?.name || "Collection";

  // Build the collection link with query parameters
  const collectionLink = hasCollection
    ? `/snapshots?businessReportId=${businessReportOutcome.business_report_id}&snapshotCollectionId=${businessReportOutcome.snapshot_collection_id}`
    : "";

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {/* Home / Snapshots */}
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/snapshots">Snapshots</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {/* Collection (if exists) */}
        {hasCollection && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={collectionLink}>{collectionName}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          </>
        )}

        {/* Current Report */}
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage className="max-w-xs truncate">
            {businessReportOutcome.fs_entry?.document_name ||
              businessReportOutcome.name ||
              businessReportOutcome.business_report.name}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};
